import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./payroll-services.css";
import { 
  BarChart3, ClipboardList, Users, Search, LineChart, Zap, 
  Coins, Scale, Target, ShieldCheck, FileEdit, Cpu, CheckSquare, 
  CreditCard, TrendingUp, Building2, MapPin, Check, 
  ArrowRight, ChevronDown
} from "lucide-react";

const features = [
  { icon: "BarChart3", title: "Payroll Processing", desc: "Accurate, timely salary disbursement with automated calculations for all components including PF, ESI, TDS, and allowances." },
  { icon: "ClipboardList", title: "Compliance Management", desc: "Stay ahead of labour law changes. We manage statutory compliance including PF, ESI, PT, TDS, and Form 16 generation." },
  { icon: "Users", title: "Employee Records", desc: "Centralised employee data management — from onboarding documents to exit formalities, all digitally maintained." },
  { icon: "Search", title: "Background Verification", desc: "Comprehensive pre-employment screening — education, employment history, criminal, and reference checks." },
  { icon: "LineChart", title: "HR Analytics", desc: "Headcount reports, cost analysis, attrition data, and custom HR dashboards to support business decisions." },
  { icon: "Zap", title: "Flexible Staffing", desc: "Contract staffing, temp-to-perm, and project-based workforce solutions to scale with your business demands." },
];

const benefits = [
  { icon: "Coins", title: "Cost Reduction", desc: "Eliminate the overhead of an in-house payroll team. Pay only for what you need." },
  { icon: "Scale", title: "Legal Compliance", desc: "Zero risk of statutory penalties. Our experts stay current on all regulations." },
  { icon: "Target", title: "Focus on Core", desc: "Free your HR team to focus on strategic initiatives, not administrative tasks." },
  { icon: "ShieldCheck", title: "Data Security", desc: "Enterprise-grade data security for all sensitive employee and payroll information." },
];

const statsData = [
  { num: "2000+", label: "Employees on Payroll" },
  { num: "100%", label: "Compliance Rate" },
  { num: "99.9%", label: "Accuracy Guaranteed" },
  { num: "200+", label: "Companies Served" },
];

const processSteps = [
  { num: "01", icon: "FileEdit", title: "Data Collection", desc: "Collect attendance, leaves, variable pay & other inputs from your HR system" },
  { num: "02", icon: "Cpu", title: "Payroll Calculation", desc: "Process gross-to-net with all statutory deductions applied accurately" },
  { num: "03", icon: "CheckSquare", title: "Compliance Filing", desc: "File PF, ESI, PT, and TDS with relevant government portals on schedule" },
  { num: "04", icon: "CreditCard", title: "Salary Disbursement", desc: "Direct bank transfers with payslips delivered to every employee on time" },
  { num: "05", icon: "TrendingUp", title: "Reports & Analytics", desc: "Comprehensive monthly reports, MIS dashboards and audit-ready records" },
];

const faqs = [
  { q: "What is Third Party Payroll?", a: "Third Party Payroll (TPP) is an arrangement where a recruitment agency like EduITalent employs workers on behalf of a client company. The workers work at the client's premises but are on EduITalent's payroll — covering salary, compliance, and HR administration." },
  { q: "What statutory compliances do you handle?", a: "We handle Provident Fund (PF), Employee State Insurance (ESI), Professional Tax (PT), TDS deductions, Form 16 issuance, and all monthly/quarterly regulatory filings." },
  { q: "Can you manage payroll for contract employees?", a: "Yes, our Third Party Payroll service is specifically designed for contract and temporary workforce. We handle the entire employee lifecycle from onboarding to exit." },
  { q: "Do you offer background verification separately?", a: "Yes, we offer standalone Background Verification services including education, employment, criminal, and reference checks." },
];

const iconMap = {
  BarChart3: <BarChart3 size={24} />,
  ClipboardList: <ClipboardList size={24} />,
  Users: <Users size={24} />,
  Search: <Search size={24} />,
  LineChart: <LineChart size={24} />,
  Zap: <Zap size={24} />,
  Coins: <Coins size={24} />,
  Scale: <Scale size={24} />,
  Target: <Target size={24} />,
  ShieldCheck: <ShieldCheck size={24} />,
  FileEdit: <FileEdit size={24} />,
  Cpu: <Cpu size={24} />,
  CheckSquare: <CheckSquare size={24} />,
  CreditCard: <CreditCard size={24} />,
  TrendingUp: <TrendingUp size={24} />,
};

export default function PayrollServices() {
  const revealRefs = useRef([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("py-shown"); }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const r = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

  return (
    <div className="py-page">

      {/* ── HERO ── */}
      <section className="py-hero">
        <div className="py-hero-bg-pattern" />
        <div className="py-hero-bg-gradient" />
        <div className="py-hero-inner">
          <div className="py-hero-content">
            <span className="py-badge">
              <Building2 size={14} className="py-badge-icon" />
              Third Party Payroll &amp; HR Outsourcing
            </span>
            <h1>
              Payroll That Works<br />
              So Your Team <span className="py-hl">Doesn't Have To</span>
            </h1>
            <p className="py-hero-desc">
              EduITalent's Third Party Payroll service takes the complexity out of payroll
              and compliance — letting your business grow without the administrative burden.
              Accurate, compliant, and always on time.
            </p>
            <div className="py-hero-btns">
              <Link to="/contact" className="py-btn-primary">
                Get a Free Quote <ArrowRight size={16} />
              </Link>
              <Link to="/services/recruitment-services" className="py-btn-ghost">All Services ↗</Link>
            </div>
          </div>
          
          <div className="py-hero-right">
            <div className="py-dashboard-mock animate-float">
              <div className="py-dash-header">
                <div className="py-dash-logo">
                  <div className="py-dash-dot"></div>
                  EduITalent Payroll
                </div>
                <span className="py-dash-badge">Active Billing Cycle</span>
              </div>
              
              <div className="py-dash-stats">
                <div className="py-dstat-card blue">
                  <span>Gross Processed</span>
                  <strong>₹1.84 Cr</strong>
                </div>
                <div className="py-dstat-card purple">
                  <span>Compliance Rate</span>
                  <strong>100.0%</strong>
                </div>
              </div>

              <div className="py-dash-profile-card">
                <div className="py-dp-header">
                  <div className="py-dp-avatar">HR</div>
                  <div className="py-dp-info">
                    <h4>Payroll Control Center</h4>
                    <span>Statutory Audit Logs</span>
                  </div>
                  <span className="py-dp-score">Audited</span>
                </div>
                <div className="py-dp-skills">
                  <span className="py-dp-skill">PF Filings</span>
                  <span className="py-dp-skill">ESI Audits</span>
                  <span className="py-dp-skill">TDS / Form 16</span>
                  <span className="py-dp-skill">PT Deductions</span>
                </div>
                <div className="py-dp-stages">
                  <div className="py-stage active">Verify</div>
                  <div className="py-stage active">Calculate</div>
                  <div className="py-stage active">Tax File</div>
                  <div className="py-stage current">Disburse</div>
                </div>
              </div>

              <span className="py-dash-list-title">Contractor Disbursement Logs</span>
              <div className="py-dash-list">
                <div className="py-dash-list-item">
                  <div className="py-dli-icon blue">
                    <CheckSquare size={14} />
                  </div>
                  <div className="py-dli-info">
                    <strong>Contractor Salaries</strong>
                    <span>Disbursed via Direct Transfer</span>
                  </div>
                </div>
                <div className="py-dash-list-item">
                  <div className="py-dli-icon purple">
                    <ShieldCheck size={14} />
                  </div>
                  <div className="py-dli-info">
                    <strong>Tax Filings (Form 24Q)</strong>
                    <span>Validated &amp; Submitted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className="py-stats-bar py-reveal" ref={r}>
        <div className="py-container">
          <div className="py-stats-grid">
            {statsData.map((s, i) => (
              <div className="py-stat" key={i} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="py-stat-icon">
                  {i === 0 && <Users size={24} />}
                  {i === 1 && <ShieldCheck size={24} />}
                  {i === 2 && <Zap size={24} />}
                  {i === 3 && <Building2 size={24} />}
                </div>
                <div className="py-stat-details">
                  <div className="py-stat-num">{s.num}</div>
                  <div className="py-stat-lbl">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ABOUT ── */}
      <section className="py-about-section">
        <div className="py-container">
          <div className="py-about-grid">
            <div className="py-about-left py-reveal" ref={r}>
              <span className="py-eyebrow">What We Offer</span>
              <h2>Complete Payroll &amp; HR Outsourcing Solutions</h2>
              <p>
                EduITalent's HR Outsourcing services are designed to provide end-to-end solutions
                — from recruitment and staffing to performance management and employee engagement.
                We believe effective HR management is critical to the success of any organisation.
              </p>
              <p>
                Our services are flexible and customisable, allowing us to tailor our approach to
                meet the specific requirements of each client. Whether you need Third Party Payroll
                for a contract workforce or complete HR management outsourcing, we deliver with
                precision and compliance.
              </p>
              <div className="py-about-checklist">
                {[
                  "Accurate salary disbursement", 
                  "Full statutory compliance", 
                  "Background verification", 
                  "Employee data management", 
                  "Replacement guarantee", 
                  "Scalable as you grow"
                ].map((item, i) => (
                  <div className="py-check-item" key={i}>
                    <Check size={14} className="py-check-icon" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="py-about-right py-reveal" ref={r}>
              <div className="py-benefits-grid">
                {benefits.map((b, i) => (
                  <div className="py-benefit-card" key={i}>
                    <div className="py-benefit-icon">
                      {iconMap[b.icon]}
                    </div>
                    <h4>{b.title}</h4>
                    <p>{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="py-features-section">
        <div className="py-container">
          <div className="py-section-hdr py-reveal" ref={r}>
            <span className="py-badge-lbl">Our Services</span>
            <h2>Everything Your HR Team Needs</h2>
            <p>A comprehensive suite of payroll and HR outsourcing services for businesses of all sizes</p>
          </div>
          <div className="py-features-grid">
            {features.map((f, i) => (
              <div className="py-feature-card py-reveal" key={i} ref={r} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="py-feat-icon">
                  {iconMap[f.icon]}
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="py-process-section">
        <div className="py-container">
          <div className="py-section-hdr py-reveal" ref={r}>
            <span className="py-badge-lbl-purple">How It Works</span>
            <h2>Our Monthly Payroll Cycle</h2>
            <p>A transparent, structured process that ensures accuracy and compliance every single month</p>
          </div>
          <div className="py-process-track">
            {processSteps.map((step, i) => (
              <div className="py-proc-step py-reveal" key={i} ref={r} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="py-proc-num">{step.num}</div>
                <div className="py-proc-icon">
                  {iconMap[step.icon]}
                </div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
                {i < processSteps.length - 1 && (
                  <div className="py-proc-arrow">
                    <ArrowRight size={20} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE BADGES ── */}
      <section className="py-compliance-section">
        <div className="py-container">
          <div className="py-comp-inner py-reveal" ref={r}>
            <div className="py-comp-left">
              <span className="py-eyebrow">Statutory Compliance</span>
              <h2>We Handle Every Regulation</h2>
              <p>Our compliance experts monitor regulatory changes and ensure your organisation is always 100% compliant with Indian and UAE labour laws.</p>
            </div>
            <div className="py-comp-badges">
              {[
                "Provident Fund (PF)", 
                "ESI", 
                "Professional Tax", 
                "TDS / Tax Deduction", 
                "Form 16 / Form 24Q", 
                "Labour Law Compliance", 
                "Gratuity Management", 
                "Leave Encashment"
              ].map((badge, i) => (
                <div className="py-comp-badge" key={i}>
                  <Check size={14} className="py-comp-check" />
                  <span>{badge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-faq-section">
        <div className="py-container">
          <div className="py-section-hdr py-reveal" ref={r}>
            <span className="py-eyebrow">Common Questions</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="py-faq-list">
            {faqs.map((faq, i) => (
              <div className={`py-faq-item py-reveal ${openFaq === i ? "py-faq-open" : ""}`} key={i} ref={r}>
                <button className="py-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <ChevronDown className={`py-faq-icon ${openFaq === i ? "py-faq-icon-active" : ""}`} size={18} />
                </button>
                {openFaq === i && (
                  <div className="py-faq-a"><p>{faq.a}</p></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-cta-section">
        <div className="py-container">
          <div className="py-cta-card py-reveal" ref={r}>
            <div className="py-cta-left">
              <h2>Simplify Your Payroll. Start Today.</h2>
              <p>
                Contact us today to learn more about our Third Party Payroll and HR Outsourcing
                services and get a customised solution for your organisation.
              </p>
              <div className="py-cta-btns">
                <Link to="/contact" className="py-btn-primary">
                  Get Free Consultation <ArrowRight size={16} />
                </Link>
                <Link to="/services/recruitment-services" className="py-btn-ghost-dark">See All Services →</Link>
              </div>
            </div>
            
            <div className="py-cta-visual">
              <div className="py-cta-badge">
                <MapPin size={14} /> India &amp; UAE Operations
              </div>
              <div className="py-cta-feature">
                <Check size={14} /> 100% Compliance Guarantee
              </div>
              <div className="py-cta-feature">
                <Check size={14} /> Dedicated Account Manager
              </div>
              <div className="py-cta-feature">
                <Check size={14} /> Same-Day Query Resolution
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
